import { AWTEventData } from '@aria/webjs-sdk/dist/src/common/DataModels';
import { AWTPropertyType } from '@aria/webjs-sdk/dist/src/common/Enums';
import { DataField } from '@ms/oteljs/dist/src/DataField';
import { DataFieldType } from '@ms/oteljs/dist/src/DataFieldType';
import { TelemetryEvent } from '@ms/oteljs/dist/src/TelemetryEvent';

// Constants
const EVENT_NAME_DOT_REPLACE_REGEX = /\./g;
const SEPARATOR_TOKEN = '.';
const DATA_TOKEN = 'Data';
const CONTRACT_TOKEN = 'zC';

// State
let eventSequence: number = 0;

export function getAriaEvent(telemetryEvent: TelemetryEvent, additionalDataFields: DataField[], timestamp?: number): AWTEventData {
  let ariaEvent: AWTEventData = {
    name: getAriaEventName(telemetryEvent.eventName),
    properties: {}
  };
  if (!telemetryEvent.telemetryProperties || !telemetryEvent.telemetryProperties.ariaTenantToken) {
    throw new Error('Unable to find ariaTenantToken for namespace.');
  }

  ariaEvent.properties!['Event.Sequence'] = { value: ++eventSequence, type: AWTPropertyType.Int64 };
  ariaEvent.properties!['Event.Name'] = telemetryEvent.eventName;
  ariaEvent.properties!['Event.Source'] = 'OTelJS';

  let timestampLocal: Date;
  if (timestamp) {
    timestampLocal = new Date(timestamp);
  } else {
    timestampLocal = new Date();
  }
  ariaEvent.properties!['Event.Time'] = { value: timestampLocal, type: AWTPropertyType.Date };

  // Event Contract
  if (telemetryEvent.eventContract) {
    ariaEvent.properties!['Event.Contract'] = telemetryEvent.eventContract.name;
    addDataFields(ariaEvent, telemetryEvent.eventContract.dataFields, false);
  }

  // These static data fields will not have "Data." prepended
  addDataFields(ariaEvent, additionalDataFields, false);

  // Per-event data fields will be prepended with "Data."
  addDataFields(ariaEvent, telemetryEvent.dataFields, true);

  return ariaEvent;
}

/**
 * Add data fields to the Aria event
 * These data fields need to be renamed from Foo.Bar to Data.Foo.Bar for user-generated fields
 * Fields arrive with names in one of two formats:
 *  1. Foo.Bar (Some true event name)
 *     This gets renamed to
 *       Data.Foo.Bar
 *  2. zC.Foo.Bar (Some label for metadata about an event)
 *     Since the underlying event will be renamed, this gets renamed to
 *       zC.Data.Foo.Bar
 *
 * @param ariaEvent The Aria event to which fields will be added
 * @param fields Fields to add
 * @param prependDataFIeld Whether or not to add the Data token
 */
function addDataFields(ariaEvent: AWTEventData, fields: DataField[] | undefined, prependDataToken: boolean) {
  if (fields) {
    fields.forEach(field => {
      let [metadataPrefix, dataToken, fieldName] = ['', '', field.name];

      const firstSeparator = field.name.indexOf(SEPARATOR_TOKEN);
      if (firstSeparator > 0 && isMetadataPrefix(field.name.substr(0, firstSeparator))) {
        metadataPrefix = field.name.substring(0, firstSeparator + 1);
        fieldName = field.name.substring(firstSeparator + 1);
      }

      if (prependDataToken) {
        dataToken = DATA_TOKEN + SEPARATOR_TOKEN;
      }

      const ariaFieldName = metadataPrefix + dataToken + fieldName;

      ariaEvent.properties![ariaFieldName] = { value: field.value, type: mapDataFieldTypeToAWTPropertyType(field.dataType) };
    });
  }
}

function mapDataFieldTypeToAWTPropertyType(otelType: DataFieldType): AWTPropertyType {
  switch (otelType) {
    case DataFieldType.String:
      return AWTPropertyType.String;
    case DataFieldType.Boolean:
      return AWTPropertyType.Boolean;
    case DataFieldType.Int64:
      return AWTPropertyType.Int64;
    case DataFieldType.Double:
      return AWTPropertyType.Double;
    default:
      const _exhaustiveCheck: never = otelType;
      throw new Error(_exhaustiveCheck);
  }
}

function getAriaEventName(eventName: string): string {
  return eventName.toLowerCase().replace(EVENT_NAME_DOT_REPLACE_REGEX, '_');
}

function isMetadataPrefix(prefix: string) {
  return prefix === CONTRACT_TOKEN;
}
